(() => {
  'use strict';
  const locales = new Set(['en', 'en-GB', 'de', 'pl', 'it', 'fr', 'es']);
  const defaultCountries = new Set(['ES','MX','GT','HN','SV','NI','CR','PA','DO','PR','CO','VE','EC','PE','BO','CL','AR','UY','PY','GQ']);
  const storageKey = 'dama_language_choice';
  function normalizeLocale(value) {
    if (String(value).toLowerCase() === 'en-gb') return 'en-GB';
    if (String(value).toLowerCase() === 'pl-pl') return 'pl';
    return locales.has(value) ? value : null;
  }
  function explicitLanguagePath(path) { return /^\/(?:en(?:-GB)?|de|pl(?:-PL)?|it|fr|es)(?:\/|$)/i.test(path || '/'); }
  function defaultLanguage({ country, territory, preference, host, enabled, preview, path, customerIsB2B }) {
    if (!enabled || preview || customerIsB2B === true || normalizeLocale(preference) || explicitLanguagePath(path)) return null;
    if (!['damacouture.com', 'www.damacouture.com'].includes(host)) return null;
    if (/^\/(?:account|customer_authentication|cart|checkout|checkouts|search)(?:\/|$)/.test(path || '/')) return null;
    const code = country === 'US' && territory === 'PR' ? 'PR' : country;
    return defaultCountries.has(code) ? 'es' : null;
  }
  function coarseGeoContext(data) {
    if (!data || typeof data !== 'object' || Array.isArray(data) || !/^[A-Z]{2}$/.test(data.country || '')) return null;
    if (![null, 'PR', 'MO'].includes(data.territory)) return null;
    if (data.territory === 'PR' && !['PR', 'US'].includes(data.country)) return null;
    if (data.territory === 'MO' && data.country !== 'MO') return null;
    return { country: data.country, territory: data.territory };
  }
  function serializedResourceUrl(url) {
    // Shopify parses the nested return URL separately: encode spaces, preserving literal plus signs.
    const search = url.searchParams.toString().replace(/\+/g, '%20');
    return url.origin + url.pathname + (search ? '?' + search : '') + url.hash;
  }
  function localizationFields({ country, language, target, languageOnly = false }) {
    const selected = normalizeLocale(language);
    if (!selected || (!languageOnly && (!/^[A-Z]{2}$/.test(country || '') || ['PR', 'CU'].includes(country)))) return null;
    try {
      const url = new URL(target);
      if (url.protocol !== 'https:' || !/^(?:www\.)?damacouture\.(?:com|de|pl|co\.uk|it|fr|es)$/.test(url.hostname) || url.username || url.password || url.port) return null;
      if (languageOnly) return { form_type: 'localization', utf8: '✓', _method: 'put', locale_code: selected === 'en-GB' ? 'en' : selected, return_to: serializedResourceUrl(url) };
      return { _method: 'PUT', country_code: country, language_code: selected === 'en-GB' ? 'en' : selected, return_to: url.pathname + (url.searchParams.size ? '?' + url.searchParams.toString().replace(/\+/g, '%20') : '') };
    } catch (_) { return null; }
  }
  function safeSpanishTarget(value, currentSearch) {
    try {
      const url = new URL(value);
      if (url.protocol !== 'https:' || !['damacouture.es', 'www.damacouture.es'].includes(url.hostname) || url.username || url.password || url.port) return null;
      url.hostname = 'damacouture.es';
      const incoming = new URLSearchParams(currentSearch);
      for (const key of new Set(incoming.keys())) {
        const retained = /^(?:utm_(?:source|medium|campaign|term|content|id)|gclid|fbclid|variant|page|sort_by)$/.test(key) || /^filter\.[A-Za-z0-9_. ()-]+$/.test(key);
        if (!retained) continue;
        url.searchParams.delete(key);
        for (const value of incoming.getAll(key)) url.searchParams.append(key, value);
      }
      url.searchParams.set('dama_auto_lang', 'es');
      return serializedResourceUrl(url);
    } catch (_) { return null; }
  }
  function safeManualTarget({ language, target, currentSearch }) {
    const selected = normalizeLocale(language);
    const host = ({ en: 'damacouture.com', 'en-GB': 'damacouture.co.uk', de: 'damacouture.de', pl: 'damacouture.pl', it: 'damacouture.it', fr: 'damacouture.fr', es: 'damacouture.es' })[selected];
    if (!host) return null;
    try {
      const url = new URL(target);
      if (url.protocol !== 'https:' || ![host, 'www.' + host].includes(url.hostname) || url.username || url.password || url.port || /^\/(?:\/|%2f|%5c)/i.test(url.pathname)) return null;
      const resource = new URL(url.pathname, 'https://' + host);
      for (const search of [url.search, currentSearch]) {
        const incoming = new URLSearchParams(search);
        for (const key of new Set(incoming.keys())) {
          const retained = /^(?:utm_(?:source|medium|campaign|term|content|id)|gclid|fbclid|variant|page|sort_by|preview_theme_id)$/.test(key) || /^filter\.[A-Za-z0-9_. ()-]+$/.test(key);
          if (!retained) continue;
          if (key === 'variant' && !/\/products\//.test(resource.pathname)) continue;
          if ((key === 'sort_by' || key.startsWith('filter.')) && !/\/collections\//.test(resource.pathname)) continue;
          if (key === 'page' && !/\/(?:collections|blogs)\//.test(resource.pathname)) continue;
          const values = incoming.getAll(key);
          if (['variant', 'preview_theme_id', 'page'].includes(key) && values.some(value => !/^\d+$/.test(value))) continue;
          resource.searchParams.delete(key);
          for (const value of values) resource.searchParams.append(key, value);
        }
      }
      resource.searchParams.set('dama_lang', selected);
      return serializedResourceUrl(resource);
    } catch (_) { return null; }
  }
  function publicLanguageTarget({ country, language, target, currentSearch }) {
    if (!/^[A-Z]{2}$/.test(country || '') || ['PR', 'CU'].includes(country)) return null;
    const retainedTarget = safeManualTarget({ language, target, currentSearch });
    if (!retainedTarget) return null;
    const resource = new URL(retainedTarget);
    if (!['en', 'en-GB'].includes(normalizeLocale(language))) resource.searchParams.set('country', country);
    return serializedResourceUrl(resource);
  }
  function englishLandingTarget({ host, pathname, search, hash = '', country, currentLocale, customerIsB2B, enabled }) {
    if (enabled !== true || customerIsB2B === true || !['en', 'en-GB'].includes(normalizeLocale(currentLocale))) return null;
    const incoming = new URLSearchParams(search);
    if (incoming.getAll('dama_lang').length !== 1) return null;
    const selected = normalizeLocale(incoming.get('dama_lang'));
    const destination = ({ en: 'damacouture.com', 'en-GB': 'damacouture.co.uk' })[selected];
    const currentHost = String(host).replace(/^www\./, '');
    if (!destination || !['damacouture.com', 'damacouture.co.uk'].includes(currentHost) || currentHost === destination) return null;
    if (!/^\/(?:$|(?:products|collections|pages|blogs|policies)(?:\/|$))/.test(pathname)) return null;
    const target = publicLanguageTarget({ country, language: selected, target: 'https://' + destination + pathname, currentSearch: search });
    if (!target) return null;
    const resource = new URL(target);
    resource.hash = hash;
    return serializedResourceUrl(resource);
  }
  function nativeCompanyLanguageTarget({ nativeLocationUrl, country, language, target, currentSearch }) {
    const selected = normalizeLocale(language);
    if (!selected || !/^[A-Z]{2}$/.test(country || '') || ['PR', 'CU'].includes(country)) return null;
    try {
      const url = new URL(target);
      const native = new URL(nativeLocationUrl);
      for (const candidate of [url, native]) {
        if (candidate.protocol !== 'https:' || !/^(?:www\.)?damacouture\.(?:com|de|pl|co\.uk|it|fr|es)$/.test(candidate.hostname) || candidate.username || candidate.password || candidate.port) return null;
      }
      if (/^\/(?:\/|%2f|%5c)/i.test(url.pathname) || native.pathname !== '/company_location/update') return null;
      if (native.searchParams.getAll('location_id').length !== 1 || !/^(?:gid:\/\/shopify\/CompanyLocation\/)?\d+$/.test(native.searchParams.get('location_id') || '')) return null;
      if ([...native.searchParams.keys()].some(key => !['location_id', 'return_to'].includes(key))) return null;
      const retainedTarget = safeManualTarget({ language: selected, target: url.href, currentSearch });
      if (!retainedTarget) return null;
      const resource = new URL(retainedTarget);
      if (!['en', 'en-GB'].includes(normalizeLocale(language))) resource.searchParams.set('country', country);
      const crossDomain = native.hostname.replace(/^www\./, '') !== url.hostname.replace(/^www\./, '');
      native.hostname = url.hostname;
      native.hash = '';
      if (crossDomain) {
        const resourcePath = serializedResourceUrl(resource).slice(resource.origin.length);
        native.searchParams.set('return_to', resourcePath);
        const login = new URL('/customer_authentication/login', resource.origin);
        login.searchParams.set('return_to', native.pathname + native.search);
        return login.href;
      }
      native.searchParams.set('return_to', serializedResourceUrl(resource));
      return native.href;
    } catch (_) { return null; }
  }
  if (typeof module !== 'undefined' && module.exports) module.exports = { defaultLanguage, safeSpanishTarget, normalizeLocale, defaultCountries, explicitLanguagePath, coarseGeoContext, localizationFields, nativeCompanyLanguageTarget, safeManualTarget, publicLanguageTarget, englishLandingTarget };
  if (typeof document === 'undefined') return;
  const node = document.getElementById('dama-es-country-config');
  if (!node) return;
  let config;
  try { config = JSON.parse(node.textContent); } catch (_) { return; }
  const params = new URLSearchParams(location.search);
  const role = window.Shopify?.theme?.role;
  const preview = config.preview === true || ['unpublished', 'development', 'demo'].includes(role) || params.has('preview_theme_id') || params.has('dama_preview') || window.Shopify?.designMode === true;
  const englishLanding = englishLandingTarget({ host: location.hostname, pathname: location.pathname, search: location.search, hash: location.hash, country: config.currentCountry, currentLocale: config.locale, customerIsB2B: config.customerIsB2B, enabled: config.enabled });
  if (englishLanding) { location.replace(englishLanding); return; }
  let preference = normalizeLocale(params.get('dama_lang'));
  try {
    if (preference) localStorage.setItem(storageKey, preference);
    else preference = normalizeLocale(localStorage.getItem(storageKey));
    if (!preference) {
      const legacy = localStorage.getItem('dama_store_pref');
      preference = normalizeLocale(({ us: 'en', uk: 'en-GB', pl: 'pl', de: 'de' })[legacy]);
    }
  } catch (_) {}
  function submitLocalization(language, target, languageOnly = false) {
    const fields = localizationFields({ country: config.currentCountry, language, target, languageOnly });
    if (!fields || typeof config.localizationAction !== 'string' || !/^\/(?:[a-zA-Z-]+\/)?localization$/.test(config.localizationAction)) return false;
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = config.localizationAction;
    form.hidden = true;
    for (const [name, value] of Object.entries(fields)) {
      const input = document.createElement('input');
      input.type = 'hidden'; input.name = name; input.value = value;
      form.appendChild(input);
    }
    document.body.appendChild(form);
    form.submit();
    return true;
  }
  function preparePublicLanguageLinks() {
    if (config.customerIsB2B === true) return;
    for (const link of document.querySelectorAll('a[data-dama-locale]')) {
      const target = publicLanguageTarget({ country: config.currentCountry, language: link.dataset.damaLocale, target: link.dataset.damaResourceHref || link.href, currentSearch: location.search });
      if (target) link.href = target;
    }
  }
  preparePublicLanguageLinks();
  document.addEventListener('dama:locale-routing-updated', preparePublicLanguageLinks);
  document.addEventListener('click', event => {
    const link = event.target.closest?.('a[data-dama-locale]');
    const selected = normalizeLocale(link?.dataset.damaLocale);
    if (!link || !selected) return;
    try {
      const retainedTarget = safeManualTarget({ language: selected, target: link.dataset.damaResourceHref || link.href, currentSearch: location.search });
      if (!retainedTarget) { event.preventDefault(); return; }
      const target = new URL(retainedTarget);
      try { localStorage.setItem(storageKey, selected); } catch (_) {}
      link.href = target.href;
      if (config.customerIsB2B === true) {
        const companyTarget = nativeCompanyLanguageTarget({ nativeLocationUrl: config.currentCompanyLocationUrl, country: config.currentCountry, language: selected, target: target.href, currentSearch: location.search });
        if (!companyTarget) { event.preventDefault(); return; }
        link.dataset.damaResourceHref = target.href;
        link.href = companyTarget;
        return;
      }
      const publicTarget = publicLanguageTarget({ country: config.currentCountry, language: selected, target: target.href, currentSearch: location.search });
      if (!publicTarget) { event.preventDefault(); return; }
      link.href = publicTarget;
      const englishDestination = selected === 'en' || selected === 'en-GB';
      if (!englishDestination && !event.ctrlKey && !event.metaKey && !event.shiftKey && !event.altKey && (!event.button || event.button === 0)) {
        if (submitLocalization(selected, target.href, true)) event.preventDefault();
      }
    } catch (_) {}
  }, true);
  if (preview || config.enabled !== true || config.customerIsB2B === true || preference || !['damacouture.com', 'www.damacouture.com'].includes(location.hostname)) return;
  if (params.has('dama_auto_lang') || explicitLanguagePath(location.pathname)) return;
  let routes;
  try { routes = JSON.parse(document.getElementById('dama-locale-urls')?.textContent || '{}').urls || {}; } catch (_) { return; }
  const target = safeSpanishTarget(routes.es, location.search);
  if (!target) return;
  fetch('https://forms.damacouture.co.uk/v1/location', { credentials: 'omit', cache: 'no-store', signal: AbortSignal.timeout(4000) })
    .then(response => response.ok ? response.json() : null)
    .then(data => {
      const context = coarseGeoContext(data);
      if (!context || defaultLanguage({ ...context, preference, host: location.hostname, enabled: config.enabled, preview, path: location.pathname, customerIsB2B: config.customerIsB2B }) !== 'es') return;
      try { if (normalizeLocale(localStorage.getItem(storageKey))) return; } catch (_) {}
      submitLocalization('es', target);
    }).catch(() => {});
})();

